import React, { useCallback, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Avatar, Grid, Tooltip, Badge } from '@mui/material';
import SnoozeIcon from '@mui/icons-material/Snooze';
import { grey } from '@mui/material/colors';

interface ResourceInfoCellProps {
  resourceId: string;
  name: string;
  initials: string;
  description?: string;
  isStandby?: boolean;
  isAdjuster?: boolean;
}

const infoStyle = { display: 'flex', gap: 8 } as const;
const cursorStyle = { cursor: 'pointer' } as const;
const tooltipTextStyle = {
  fontSize: '15px',
  margin: '0',
  padding: '0',
} as const;
const descriptionStyle = {
  fontSize: '12.8px',
  color: grey[700],
  fontWeight: 'bold',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  maxWidth: '150px',
} as const;

/**
 * ResourceInfoCell - Custom cell renderer for resource column in scheduler
 *
 * Renders:
 * - Blue Avatar with initials (clickable → navigates to resource details)
 * - Standby badge icon if resource is in standby state
 * - Resource name
 * - Description with tooltip
 *
 * Avatar is clickable and navigates to the resource detail page.
 */
export const ResourceInfoCell: React.FC<ResourceInfoCellProps> = memo(
  ({ resourceId, name, initials, description, isStandby, isAdjuster }) => {
    const navigate = useNavigate();

    // Navigate to resource details blade
    const openResource = useCallback(() => {
      if (!isAdjuster && resourceId) {
        navigate(`/scheduler/resources/${resourceId}?tab=0`);
      }
    }, [navigate, isAdjuster, resourceId]);

    return (
      <div className="info" style={infoStyle}>
        <div onClick={openResource} style={cursorStyle}>
          <Badge
            overlap="circular"
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            badgeContent={
              <Tooltip
                title={
                  <p style={tooltipTextStyle}>Resource is in StandBy state</p>
                }
              >
                <SnoozeIcon sx={{ color: 'black' }} />
              </Tooltip>
            }
            invisible={!isStandby}
          >
            <Avatar
              className="b-resource-avatar b-resource-initials"
              role="presentation"
              sx={{ bgcolor: '#2196f3' }}
            >
              {initials}
            </Avatar>
          </Badge>
        </div>
        <Grid
          container
          direction="column"
          justifyContent="center"
          alignItems="flex-start"
        >
          <div className="name">
            <strong>{name}</strong>
          </div>
          {description && (
            <Tooltip title={<p style={tooltipTextStyle}>{description}</p>}>
              <div style={descriptionStyle}>{description}</div>
            </Tooltip>
          )}
        </Grid>
      </div>
    );
  }
);

ResourceInfoCell.displayName = 'ResourceInfoCell';

export default ResourceInfoCell;
